<p-dialog [(visible)]="showMissingWarning" [modal]="true" [closable]="true" [style]="{width: '500px'}">
	<ng-template pTemplate="header">
		<i class="pi pi-exclamation-triangle" style="color: #f59e0b; font-size: 1.4rem; margin-right: 0.5rem;"></i>
		<span class="p-dialog-title">Missing Substitution Fields</span>
	</ng-template>
	<p>The campaign has been saved. However, the following employers are missing substitution fields and will be skipped at send time:</p>
	<ul style="margin-top: 0.75rem; padding-left: 1.5rem;">
		<li *ngFor="let e of missingEmployers">{{ e.employerName }}</li>
	</ul>
	<ng-template pTemplate="footer">
		<button pButton label="Got It" class="neutral" (click)="showMissingWarning = false"></button>
	</ng-template>
</p-dialog>
<div class="campaign-page" *ngIf="campaign">
	<!-- Opened from a drip program: says whether Save edits in place or creates a private copy for that program. -->
	<div class="drip-banner" *ngIf="dripContext" [class.warn]="cloneBlocked" [class.copy]="willClone && !cloneBlocked">
		<i class="pi" [ngClass]="cloneBlocked ? 'pi-exclamation-triangle' : (willClone ? 'pi-copy' : 'pi-info-circle')"></i>
		<div class="drip-banner-body">
			<!-- Used by this program only -->
			<ng-container *ngIf="!willClone">
				<strong>This email is used only by this drip program.</strong>
				Changes you save here apply to this program only.
			</ng-container>
			<!-- Shared: Save makes a private copy for this program -->
			<ng-container *ngIf="willClone && !cloneBlocked">
				<strong>This email is used by other drip programs too.</strong>
				Saving creates a copy for this program; the others keep the original.
				<div class="clone-name">
					<label for="clone-name">Name for the copy</label>
					<input pInputText id="clone-name" [(ngModel)]="cloneName" (ngModelChange)="markDirty('cloneName')" />
					<p *ngIf="errors.cloneName" class="error">{{ errors.cloneName }}</p>
				</div>
			</ng-container>
			<!-- Shared and already sent on this program: can't be copied -->
			<ng-container *ngIf="cloneBlocked">
				<strong>This email is used by other drip programs and has already gone out on this one.</strong>
				It can't be changed here. To change it everywhere, edit it from the campaign list.
			</ng-container>
		</div>
	</div>
	<div class="tabs" role="tablist">
		<button *ngFor="let tab of tabs" type="button" role="tab" class="tab" [class.active]="activeTab === tab.id" [attr.aria-selected]="activeTab === tab.id" (click)="selectTab(tab.id)">
			{{ tab.label }}
			<span class="tab-alert pi pi-exclamation-circle" *ngIf="tabHasErrors(tab.id)" title="This tab has errors"></span>
		</button>
	</div>

	<!-- Details: who gets it and when. One label column, one field column. -->
	<div class="details tab-panel" role="tabpanel" [class.is-hidden]="activeTab !== 'details'">
		<label class="form-label" for="campaign-name">Campaign Name</label>
		<div class="form-field">
			<input pInputText id="campaign-name" class="name-input" [(ngModel)]="campaign.campaignName" (ngModelChange)="markDirty('campaignName')" />
			<p *ngIf="errors.campaignName" class="error">{{ errors.campaignName }}</p>
		</div>

		<div class="form-label">Status</div>
		<div class="form-field">
			<div class="activate-button {{ statusId == 4 ? 'inactive' : 'active' }}" (click)="toggleActive()">
				<ng-container *ngIf="statusId == 4">
					<i class="pi pi-times"></i>
					<span>Deactivated</span>
				</ng-container>
				<ng-container *ngIf="statusId != 4">
					<i class="pi pi-check"></i>
					<span>Activated</span>
				</ng-container>
			</div>
		</div>

		<div class="form-label">Employers</div>
		<div class="form-field choices">
			<div class="choice">
				<p-radioButton inputId="employers-all" [(ngModel)]="employerType" value="all" (ngModelChange)="markDirty('employerType')" [disabled]="campaign?.emailTypeID === emailType.Drip"></p-radioButton>
				<label for="employers-all">All employers</label>
			</div>
			<div class="choice">
				<p-radioButton inputId="employers-selected" [(ngModel)]="employerType" value="selected" (ngModelChange)="markDirty('employerType')" [disabled]="campaign?.emailTypeID === emailType.Drip"></p-radioButton>
				<label for="employers-selected">Selected employers</label>
				<div class="button neutral" (click)="selectEmployers()" [class.disabled]="campaign?.emailTypeID === emailType.Drip">Select Employers</div>
			</div>
			<div class="choice-note" *ngIf="selectedEmployerNames">{{ selectedEmployerNames }}</div>
			<div class="choice-note muted" *ngIf="campaign?.emailTypeID === emailType.Drip">Drip campaigns must target all employers.</div>
		</div>

		<div class="form-label">
			Schedule
			<span class="material-symbols-outlined info-icon" [pTooltip]="'Scheduling for Drip programs is handled on the Drip Program configuration screen'" tooltipPosition="top" [appendTo]="'body'" tooltipStyleClass="tooltip-wrap">info</span>
		</div>
		<div class="form-field choices">
			<div class="choice">
				<p-radioButton inputId="schedule-single" [(ngModel)]="campaign.emailTypeID" [value]="emailType.Single" (ngModelChange)="markDirty('emailType'); setCampaignType(emailType.Single)"></p-radioButton>
				<label for="schedule-single">One time on</label>
				<p-calendar [(ngModel)]="campaign.deliveryDateTime" [showTime]="true" [hourFormat]="'12'" (ngModelChange)="markDirty('deliveryDateTime')"></p-calendar>
			</div>
			<p *ngIf="errors.deliveryDateTime" class="error">{{ errors.deliveryDateTime }}</p>
			<div class="choice">
				<p-radioButton inputId="schedule-drip" [(ngModel)]="campaign.emailTypeID" [value]="emailType.Drip" (ngModelChange)="markDirty('emailType'); setCampaignType(emailType.Drip)"></p-radioButton>
				<label for="schedule-drip">Drip</label>
			</div>
		</div>
	</div>

	<!-- SMS: the text message sent alongside (or instead of) the email. -->
	<div class="sms tab-panel" role="tabpanel" [class.is-hidden]="activeTab !== 'sms'">
		<div class="sms-details">
			<div class="sms-message">
				<label>
					SMS Message
					<span class="material-symbols-outlined info-icon" [pTooltip]="'Text shown before the full-email link in the SMS.'" tooltipPosition="top" [appendTo]="'body'" tooltipStyleClass="tooltip-wrap">info</span>
				</label>
				<div>
					<textarea pInputTextarea [(ngModel)]="campaign.smsMessage" (ngModelChange)="markDirty('smsMessage')" rows="6" placeholder="Enter SMS message content..." [style]="{ width: '100%', resize: 'vertical' }"></textarea>
					<p *ngIf="errors.smsMessage" class="error">{{ errors.smsMessage }}</p>
				</div>
			</div>
		</div>
	</div>

	<!-- Email: the editor gets the whole viewport. Kept rendered (not *ngIf) while on the
	     Details tab so Unlayer keeps its unsaved design and Save can export it. -->
	<div class="email-workspace" role="tabpanel" [class.is-hidden]="activeTab !== 'email'">
		<div class="subject-row">
			<label for="email-subject">Subject</label>
			<input pInputText id="email-subject" [(ngModel)]="campaign.eMailSubject" (ngModelChange)="markDirty('eMailSubject')" />
			<p *ngIf="errors.eMailSubject" class="error">{{ errors.eMailSubject }}</p>
		</div>
		<div class="workspace-toolbar">
			<p-selectButton [options]="modeOptions" [(ngModel)]="modeSelection" (onChange)="requestModeChange($event.value)" optionLabel="label" optionValue="value" [allowEmpty]="false">
				<ng-template let-item pTemplate>
					<i [class]="item.icon"></i>&nbsp;{{ item.label }}
				</ng-template>
			</p-selectButton>
			<p-dropdown [options]="templates" [(ngModel)]="selectedTemplate" (onChange)="templateSelected($event)" optionLabel="templateName" [autoDisplayFirst]="false" placeholder="Start from template…" [style]="{ width: '320px' }"></p-dropdown>
			<span class="spacer"></span>
			<ng-container *ngIf="isHtml">
				<span class="importing" *ngIf="expertEditor?.importing === true"><i class="pi pi-spin pi-spinner"></i> Importing…</span>
				<span class="material-symbols-outlined info-icon" [pTooltip]="expertHelp" [escape]="false" tooltipPosition="bottom" [appendTo]="'body'" tooltipStyleClass="tooltip-help">info</span>
				<button pButton type="button" [text]="true" icon="pi pi-images" label="Copy images to storage" (click)="expertEditor?.reimportImages()" [disabled]="!campaign.eMailContent"></button>
				<button pButton type="button" [text]="true" icon="pi pi-upload" label="Upload an image" (click)="expertEditor?.openImagePicker()"></button>
			</ng-container>
		</div>

		<p *ngIf="errors.eMailContent || errors.emailJSON" class="workspace-error">{{ errors.eMailContent || errors.emailJSON }}</p>

		<div class="designer" *ngIf="!isHtml">
			<div class="sidebar">
				<app-substitution-fields [showVimeoOption]="true"></app-substitution-fields>
			</div>
			<div class="designer-main">
				<email-editor (loaded)="editorLoaded($event)" [options]="editorOptions" [minHeight]="'0px'"></email-editor>
			</div>
		</div>

		<app-expert-html-editor *ngIf="isHtml" [(html)]="campaign.eMailContent" (changed)="isDirty = true"></app-expert-html-editor>
	</div>
</div>

<p-dialog [(visible)]="showEmployeeChooser" [modal]="true" [style]="{ width: '50vw' }">
	<ng-template pTemplate="header">
		<span class="header">Employee Chooser</span>
	</ng-template>

	<div class="employer-chooser">
		<div class="search">
			<input pInputText type="text" [(ngModel)]="employeeSearchTerm" (input)="filterEmployers()" placeholder="Search Employees" />
		</div>
		<div class="employer-list">
			<ul>
				<li *ngFor="let employee of filteredEmployers">
					<p-checkbox [(ngModel)]="employee.selected" [binary]="true"></p-checkbox>
					{{ employee.employerName }}
				</li>
			</ul>
		</div>
	</div>

	<ng-template pTemplate="footer">
		<div class="actions">
			<div class="button neutral" (click)="close()">Cancel</div>
			<div class="button success" (click)="confirm()">Save</div>
		</div>
	</ng-template>
</p-dialog>

<p-dialog [(visible)]="testFormVisible" [header]="'Test Campaign'" [style]="{ width: '35%' }">
	<p>Test the campaign by sending communications to yourself. Enter your email address and phone number.</p>
	<div class="test-form">
		<div class="field">
			<label>Email Address</label>
			<input pInputText [(ngModel)]="testEmail" />
		</div>
		<div class="field">
			<label>Phone Number</label>
			<input pInputText [(ngModel)]="testPhone" />
		</div>
		<div class="field">
			<label>Test as this Employer</label>
			<p-dropdown [options]="allEmployers" [(ngModel)]="testEmployer" [optionLabel]="'employerName'" [autoDisplayFirst]="false" [appendTo]="'body'"></p-dropdown>
		</div>
	</div>
	<footer class="test-footer">
		<div class="button neutral" (click)="testFormVisible = false">Cancel</div>
		<div class="button success" (click)="sendTest()">Send Test</div>
	</footer>
</p-dialog>
